import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { Edge, Component as PackageComponent } from '../../package.models';
import { getComponentId } from '../../helpers/components.helpers';
import { DESIGNER_SETTINGS } from '../../../constants/designer_settings';
import { AppState } from '../../../store';
import { autoAlign } from '../../store/package-designer.actions';
import { ShortcutsService } from '../../services/shortcuts.service';

@Component({
  selector: 'designer-map',
  template: `
    <div class="designer-map-new" #designerMap>
      <div class="designer-map-wrapper" #designerMapWrapper>
        <div
          class="designer-map-viewport-new"
          #viewport
          cdkDrag
          cdkDragBoundary=".designer-map-wrapper"
          (cdkDragMoved)="onDragMoved($event)"
        ></div>
        <svg id="svg-map" class="designer-map-svg" version="1.1" #svgMap>
          <g class="designer-map-stage">
            <line
              designer-map-edge
              class="designer-map-edge"
              *ngFor="let edge of edges; trackBy: identify"
              [edgeId]="edge.id"
              [scale]="scale"
            ></line>
            <rect
              designer-map-component
              [ngClass]="getComponentId(component).includes('note') ? 'designer-map-note' : 'designer-map-component'"
              *ngFor="let component of components; trackBy: identifyComponent"
              [componentId]="getComponentId(component)"
              [scale]="scale"
            ></rect>
          </g>
          <rect class="designer-map-overlay designer-map-overlay-top" #overlayTop></rect>
          <rect class="designer-map-overlay designer-map-overlay-right" #overlayRight></rect>
          <rect class="designer-map-overlay designer-map-overlay-bottom" #overlayBottom></rect>
          <rect class="designer-map-overlay designer-map-overlay-left" #overlayLeft></rect>
        </svg>
        <div class="designer-map-buttons">
          <button
            id="button-align"
            [matTooltip]="'designer.controls.actions.auto_align' | translate"
            (click)="autoAlign()"
          >
            <svg width="25px" height="25px" viewBox="0 0 25 25">
              <g>
                <path
                  style="fill: #ffffff;"
                  d="M23,1c0.6,0,1,0.4,1,1v21c0,0.6-0.4,1-1,1H2c-0.6,0-1-0.4-1-1V2c0-0.6,0.4-1,1-1H23 M23,0H2
						C0.9,0,0,0.9,0,2v21c0,1.1,0.9,2,2,2h21c1.1,0,2-0.9,2-2V2C25,0.9,24.1,0,23,0L23,0z"
                />
              </g>
              <rect x="12" y="4" style="fill: #ffffff;" width="1" height="17" />
              <g>
                <rect x="7" y="7" style="fill: #5d5d5d;" width="10" height="4" />
                <path style="fill: #ffffff;" d="M17,7v3H8V7H17 M18,6H7v5h11V6L18,6z" />
              </g>
              <g>
                <rect x="7" y="14" style="fill: #5d5d5d;" width="10" height="4" />
                <path style="fill: #ffffff;" d="M17,14v3H8v-3H17 M18,13H7v5h11V13L18,13z" />
              </g>
            </svg>
          </button>
          <a (click)="openConsole()">
            <button id="button-terminal" [matTooltip]="'designer.controls.actions.x_console' | translate">
              <svg width="24px" height="25px" viewBox="0 0 24 25">
                <g>
                  <path
                    style="fill: #ffffff;"
                    d="M21,1c1.1,0,2,0.9,2,2v19c0,1.1-0.9,2-2,2H3c-1.1,0-2-0.9-2-2V3c0-1.1,0.9-2,2-2H21 M21,0H3
                          C1.3,0,0,1.3,0,3v19c0,1.7,1.3,3,3,3h18c1.7,0,3-1.3,3-3V3C24,1.3,22.7,0,21,0L21,0z"
                  />
                </g>
                <g>
                  <path style="fill: #ffffff;" d="M5,13.1v-0.8L10,9v0.9l-4,2.7l4,2.7v0.9L5,13.1z" />
                </g>
                <g>
                  <path style="fill: #ffffff;" d="M19,12.2V13l-5,3.3v-0.9l4-2.7l-4-2.7V9L19,12.2z" />
                </g>
              </svg>
            </button>
          </a>
        </div>
      </div>
    </div>
  `,
})
export class DesignerMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() components: PackageComponent[];
  @Input() edges: Edge[];
  @Input() designerPackageWidth: number;
  @Input() designerPackageHeight: number;

  @ViewChild('designerMap') designerMap: ElementRef<HTMLElement>;
  @ViewChild('designerMapWrapper') designerMapWrapper: ElementRef<HTMLElement>;
  @ViewChild('svgMap') svgMap: ElementRef<HTMLElement>;
  @ViewChild('viewport') viewport: ElementRef<HTMLElement>;
  @ViewChild('overlayTop') overlayTop: ElementRef<HTMLElement>;
  @ViewChild('overlayBottom') overlayBottom: ElementRef<HTMLElement>;
  @ViewChild('overlayLeft') overlayLeft: ElementRef<HTMLElement>;
  @ViewChild('overlayRight') overlayRight: ElementRef<HTMLElement>;

  getComponentId = getComponentId;
  scale = 1;
  width = 0;
  height = 0;
  x = 0;
  y = 0;
  dragPosition = { x: 0, y: 0 };
  shortcutSubscription: Subscription;

  constructor(
    private store: Store<AppState>,
    @Inject('$rootScope') private $rootScope: any,
    private shortcutsService: ShortcutsService,
  ) {}

  ngAfterViewInit() {
    document.querySelector('.designer-scroll').addEventListener('scroll', this.setViewPosition.bind(this));

    this.shortcutSubscription = this.shortcutsService.addShortcut({ keys: 'meta.f' }).subscribe(() => {
      this.autoAlign();
    });

    this.setSize();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.designerPackageWidth?.currentValue !== changes.designerPackageWidth?.previousValue ||
      changes.designerPackageHeight?.currentValue !== changes.designerPackageHeight?.previousValue
    ) {
      setTimeout(() => {
        if (this.designerMap?.nativeElement) {
          this.setSize();
        }
      });
    }
  }

  // eslint-disable-next-line class-methods-use-this
  identify(index: number, item: any) {
    return item.id;
  }

  // eslint-disable-next-line class-methods-use-this
  identifyComponent(index: number, item: any) {
    return getComponentId(item);
  }

  setSize() {
    const designerElement = document.querySelector('.designer') as HTMLElement;
    const designerWidth = designerElement.offsetWidth;
    const designerHeight = designerElement.offsetHeight;
    this.width = Math.round(designerWidth * DESIGNER_SETTINGS.PANEL_SCALE);
    this.height = Math.round(designerHeight * DESIGNER_SETTINGS.PANEL_SCALE);

    this.designerMap.nativeElement.style.width = `${this.width + 32}px`;
    this.designerMap.nativeElement.style.height = `${this.height + 2}px`;

    this.designerMapWrapper.nativeElement.style.width = `${this.width}px`;
    this.designerMapWrapper.nativeElement.style.height = `${this.height}px`;

    this.svgMap.nativeElement.setAttribute('width', String(this.width));
    this.svgMap.nativeElement.setAttribute('height', String(this.height));
    this.svgMap.nativeElement.setAttribute('viewBox', `0 0 ${this.width} ${this.height}`);

    const designerRatio = this.designerPackageWidth / this.designerPackageHeight;
    const mapRatio = this.width / this.height;
    this.scale = 1;

    if (designerRatio < mapRatio) {
      this.scale = this.height / (this.designerPackageHeight * DESIGNER_SETTINGS.PANEL_SCALE);
    } else {
      this.scale = this.width / (this.designerPackageWidth * DESIGNER_SETTINGS.PANEL_SCALE);
    }

    this.viewport.nativeElement.style.width = `${Math.round(this.width * this.scale)}px`;
    this.viewport.nativeElement.style.height = `${Math.round(this.height * this.scale)}px`;

    if (this.designerPackageHeight && this.designerPackageWidth) {
      this.setViewPosition();
    }
  }

  setViewPosition() {
    const { scrollTop, scrollLeft } = document.querySelector('.designer-scroll');
    this.x = scrollLeft * this.scale * DESIGNER_SETTINGS.PANEL_SCALE;
    this.y = scrollTop * this.scale * DESIGNER_SETTINGS.PANEL_SCALE;

    const overlayLeft = {
      x: 0,
      y: 0,
      width: this.x - 1 < 0 ? 0 : this.x - 1,
      height: this.height,
    };
    this.overlayLeft.nativeElement.setAttribute('x', String(overlayLeft.x));
    this.overlayLeft.nativeElement.setAttribute('y', String(overlayLeft.y));
    this.overlayLeft.nativeElement.setAttribute('width', String(overlayLeft.width));
    this.overlayLeft.nativeElement.setAttribute('height', String(overlayLeft.height));

    const overlayTop = {
      x: this.x - 1 < 0 ? 0 : this.x - 1,
      y: 0,
      width: this.width * this.scale + 1,
      height: this.y - 1 < 0 ? 0 : this.y - 1,
    };
    this.overlayTop.nativeElement.setAttribute('x', String(overlayTop.x));
    this.overlayTop.nativeElement.setAttribute('y', String(overlayTop.y));
    this.overlayTop.nativeElement.setAttribute('width', String(overlayTop.width));
    this.overlayTop.nativeElement.setAttribute('height', String(overlayTop.height));

    const overlayBottom = {
      x: this.x - 1 < 0 ? 0 : this.x - 1,
      y: this.y + this.height * this.scale,
      width: this.width * this.scale + 1,
      height: this.height - this.y - this.height * this.scale,
    };
    overlayBottom.height = overlayBottom.height < 0 ? 0 : overlayBottom.height;
    this.overlayBottom.nativeElement.setAttribute('x', String(overlayBottom.x));
    this.overlayBottom.nativeElement.setAttribute('y', String(overlayBottom.y));
    this.overlayBottom.nativeElement.setAttribute('width', String(overlayBottom.width));
    this.overlayBottom.nativeElement.setAttribute('height', String(overlayBottom.height));

    const overlayRight = {
      x: this.x + this.width * this.scale,
      y: 0,
      width: this.width - this.width * this.scale - this.x,
      height: this.height,
    };
    overlayRight.width = overlayRight.width < 0 ? 0 : overlayRight.width;
    this.overlayRight.nativeElement.setAttribute('x', String(overlayRight.x));
    this.overlayRight.nativeElement.setAttribute('y', String(overlayRight.y));
    this.overlayRight.nativeElement.setAttribute('width', String(overlayRight.width));
    this.overlayRight.nativeElement.setAttribute('height', String(overlayRight.height));

    this.viewport.nativeElement.style.transform = `translate(${Math.round(this.x) - 0.5}px, ${
      Math.round(this.y) - 0.5
    }px)`;
    this.dragPosition = { x: Math.round(this.x) - 0.5, y: Math.round(this.y) - 0.5 };
  }

  onDragMoved(event) {
    const { distance } = event;

    const top = distance.y / this.scale / DESIGNER_SETTINGS.PANEL_SCALE;
    const left = distance.x / this.scale / DESIGNER_SETTINGS.PANEL_SCALE;

    const desginerScroll = document.querySelector('.designer-scroll') as HTMLElement;

    desginerScroll.scrollLeft = left;
    desginerScroll.scrollTop = top;
  }

  openConsole() {
    const url = this.$rootScope.getHref('app.account.console');
    window.open(url, '_blank');
  }

  autoAlign() {
    this.store.dispatch(autoAlign({ width: this.width }));
  }

  ngOnDestroy() {
    document.querySelector('.designer-scroll').removeEventListener('scroll', this.setViewPosition.bind(this));

    if (this.shortcutSubscription) {
      this.shortcutSubscription.unsubscribe();
    }
  }
}
